import { Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import { canAdmin } from '../../auth/adminAccess';
import { WalletDrawer } from './WalletPages';
import { thb } from './walletApi';
import './system-wallet-topup.css';

type Snapshot = { phone: string; amount: number; reason: string; baht: number };
export function SystemWalletTopup({ onComplete }: { onComplete: () => void }) {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const [baht, setBaht] = useState('');
  const [phone, setPhone] = useState(session?.tel || '');
  const [reason, setReason] = useState('เติมเงินเข้ากระเป๋าระบบ');
  const [code, setCode] = useState('');
  const [rate, setRate] = useState<number | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const reference = useRef('');
  useEffect(() => {
    if (!open || !session?.expert_id) return;
    const controller = new AbortController();
    setRate(null);
    apiRequest<{ rate: { thb_per_point: number } }>('/admin/economy/point-exchange-rate', {
      query: { admin_id: session.expert_id }, signal: controller.signal,
    }).then(data => {
      const value = Number(data.rate.thb_per_point);
      if (!(value > 0)) throw new Error('ไม่พบอัตราแลกเปลี่ยน กรุณาลองใหม่');
      setRate(value);
    }).catch(e => { if (!controller.signal.aborted) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ'); });
    return () => controller.abort();
  }, [open, session?.expert_id]);
  if (!canAdmin(session, 'finance')) return null;
  const close = () => { if (!inFlight.current) setOpen(false); };
  const begin = () => {
    setSnapshot(null); setCode(''); setError(''); setSuccess('');
    reference.current = `system_topup_${crypto.randomUUID()}`;
    setOpen(true);
  };
  const sendCode = async () => {
    if (inFlight.current || !session || rate === null) return;
    const value = Number(baht), points = value / rate;
    if (!Number.isFinite(value) || value <= 0 || !Number.isSafeInteger(Math.round(points)) || Math.abs(points - Math.round(points)) > 0.000001) {
      setError('กรุณาระบุจำนวนเงินที่มากกว่า 0 และตรงกับอัตราแลกคะแนนปัจจุบัน'); return;
    }
    if (!/^\+?\d[\d\s-]{7,23}$/.test(phone.trim()) || !reason.trim()) {
      setError('กรุณาระบุเบอร์รับ SMS และเหตุผลให้ครบ'); return;
    }
    const next = { phone: phone.trim(), amount: Math.round(points), reason: reason.trim(), baht: value };
    inFlight.current = true; setBusy(true); setError('');
    try {
      await apiRequest('/admin/economy/system-wallet/topup/confirmation-sms', { method: 'POST', body: {
        ...next, admin_id: session.expert_id, admin_name: session.display_name,
      } });
      setSnapshot(next); setCode('');
    } catch (e) { setError(e instanceof Error ? e.message : 'ส่งรหัสไม่สำเร็จ'); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const confirm = async () => {
    if (inFlight.current || !snapshot || !session || !/^\d{4,12}$/.test(code)) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      await apiRequest('/admin/economy/system-wallet/topup', { method: 'POST', body: {
        amount: snapshot.amount, reason: snapshot.reason, admin_note: snapshot.reason,
        admin_id: session.expert_id, reference_id: reference.current,
        confirmation_phone: snapshot.phone, confirmation_code: code,
      } });
      setSuccess(`เติมเงิน ${thb(snapshot.baht, 2)} สำเร็จ`);
      setSnapshot(null); setCode(''); onComplete();
    } catch (e) { setError(e instanceof Error ? e.message : 'เติมเงินไม่สำเร็จ'); }
    finally { inFlight.current = false; setBusy(false); }
  };
  return <>
    <button className="button button--primary" type="button" onClick={begin}><Plus size={18}/> เติม System Wallet</button>
    {open && <WalletDrawer title="เติม System Wallet" subtitle="บันทึกเงินเข้ากระเป๋าระบบ · ยืนยันด้วย SMS OTP" onClose={close}>
      <section className="panel system-topup-form">
        <p>ผู้ทำรายการ: <strong>{session?.display_name || session?.expert_id}</strong></p>
        <p>วันที่: {new Intl.DateTimeFormat('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'long' }).format(new Date())}</p>
        {error && <p className="system-topup-error" role="alert">{error}</p>}
        {success ? <><p role="status" className="system-topup-success">{success}</p><button className="button" onClick={close}>เสร็จสิ้น</button></> : <>
          <label>จำนวนเงิน (บาท)<input type="number" min="0.01" step="0.01" value={baht} disabled={busy || !!snapshot} onChange={e => setBaht(e.target.value)} placeholder="เช่น 10000"/></label>
          {rate !== null && <small>1 คะแนน = {thb(rate, 2)} · ระบบจะบันทึก {Number(baht) > 0 ? (Number(baht) / rate).toLocaleString('th-TH', { maximumFractionDigits: 6 }) : '0'} คะแนน</small>}
          <label>เหตุผล<input maxLength={300} value={reason} disabled={busy || !!snapshot} onChange={e => setReason(e.target.value)}/></label>
          <label>เบอร์โทรรับรหัส SMS<input type="tel" autoComplete="tel" value={phone} disabled={busy || !!snapshot} onChange={e => setPhone(e.target.value)} placeholder="08xxxxxxxx"/></label>
          {snapshot ? <>
            <p role="status">ส่งรหัสไปที่ {snapshot.phone} แล้ว · ยอดเติม {thb(snapshot.baht, 2)}</p>
            <label>รหัสยืนยัน SMS<input autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={12} value={code} disabled={busy} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}/></label>
            <div className="system-topup-actions"><button className="button" disabled={busy} onClick={() => { setSnapshot(null); setCode(''); setError(''); }}>แก้ไขข้อมูล / ขอรหัสใหม่</button><button className="button button--primary" disabled={busy || !/^\d{4,12}$/.test(code)} onClick={confirm}>{busy ? 'กำลังบันทึก…' : `ยืนยันเติม ${thb(snapshot.baht, 2)}`}</button></div>
          </> : <button className="button button--primary" disabled={busy || rate === null} onClick={sendCode}>{busy ? 'กำลังส่งรหัส…' : rate === null ? 'กำลังโหลดอัตราแลกเปลี่ยน…' : 'ส่งรหัสยืนยัน SMS'}</button>}
        </>}
      </section>
    </WalletDrawer>}
  </>;
}
